import { useState, type FormEvent } from "react";
import type { Api } from "../data/api.ts";
import { DEFAULT_ALEAS, MACHINE_SPECS, PACKAGING_SUGGESTIONS, SPEED_UNIT, TYPE_OPS, type FieldDef } from "../data/catalog.ts";
import { makeDemo } from "../data/demoData.ts";
import {
  DEFAULT_PERMISSIONS, FORMATS, MACHINE_TYPE_LABEL, OPERATION_LABEL, OPERATOR_ROLE_LABEL, PERMISSION_LABEL, ROLE_LABEL,
  type CatalogItem, type Machine, type MachineType, type Operation, type Operator, type OperatorRole, type ParamValue,
  type Permission, type Role, type WorkingHours,
} from "../data/types.ts";
import { buildSheets, exportFileName } from "../lib/exportData.ts";
import { fmtDateTime } from "../lib/time.ts";
import { buildCsv, buildXlsx, download, zip } from "../lib/xlsx.ts";
import { Badge, Empty, ErrorBox, Field, Loading, Modal, attempt, navigate, toast, useApp, useData } from "../ui/kit.tsx";
import { Header } from "./Planning.tsx";

const TABS: { key: string; label: string; perm?: Permission }[] = [
  { key: "imprimerie", label: "Imprimerie et horaires" },
  { key: "machines", label: "Machines" },
  { key: "conditionnements", label: "Conditionnements" },
  { key: "aleas", label: "Aléas des postes" },
  { key: "operateurs", label: "Opérateurs" },
  { key: "tablettes", label: "Tablettes des postes" },
  { key: "equipe", label: "Équipe et droits" },
  { key: "export", label: "Export" },
];

export function SettingsPage({ tab }: { tab: string }) {
  return (
    <div className="page">
      <Header title="Paramètres" />
      <div className="tabs" role="tablist">
        {TABS.map((t) => <a key={t.key} role="tab" aria-selected={tab === t.key} href={`#/parametres/${t.key}`} className={tab === t.key ? "on" : ""}>{t.label}</a>)}
      </div>
      <div className="tab-body" key={tab}>
        {tab === "machines" ? <Machines />
          : tab === "conditionnements" ? <Packaging />
          : tab === "aleas" ? <Aleas />
          : tab === "operateurs" ? <Operators />
          : tab === "tablettes" ? <Tablets />
          : tab === "equipe" ? <Team />
          : tab === "export" ? <ExportTab />
          : <OrgSettings />}
      </div>
    </div>
  );
}

function ReadOnly({ what }: { what: string }) {
  const { role } = useApp();
  return <div className="readonly">Lecture seule : votre rôle ({ROLE_LABEL[role]}) ne permet pas de modifier {what}.</div>;
}

const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

// ---------------------------------------------------------------------------
function OrgSettings() {
  const { api, org, refreshOrg, can } = useApp();
  const edit = can("machines");
  const [name, setName] = useState(org.name);
  const [siret, setSiret] = useState(org.siret || "");
  const [hours, setHours] = useState<WorkingHours>(org.hours);
  const days = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await attempt(() => api.updateOrg({ name: name.trim(), siret: siret.trim() || null, hours }), "Enregistré");
    if (ok !== undefined) await refreshOrg();
  };
  return (
    <form className="card form-card" onSubmit={submit}>
      {!edit && <ReadOnly what="l'imprimerie" />}
      <fieldset disabled={!edit} className="bare">
        <div className="grid-2">
          <Field label="Nom de l'imprimerie"><input value={name} onChange={(e) => setName(e.target.value)} required /></Field>
          <Field label="SIRET"><input value={siret} onChange={(e) => setSiret(e.target.value)} /></Field>
        </div>
        <h2 className="section-title">Horaires de l'atelier</h2>
        <p className="muted small">Le planning ne place du travail que pendant ces heures, et saute les pauses.</p>
        <div className="days">
          {days.map((d, i) => (
            <label key={d} className={`day ${hours.days.includes(i + 1) ? "on" : ""}`}>
              <input type="checkbox" checked={hours.days.includes(i + 1)}
                onChange={(e) => setHours({ ...hours, days: e.target.checked ? [...hours.days, i + 1].sort() : hours.days.filter((x) => x !== i + 1) })} />{d}
            </label>
          ))}
        </div>
        <div className="ranges">
          {hours.ranges.map((r, i) => (
            <div key={i} className="range">
              <input type="time" value={r.start} onChange={(e) => setHours({ ...hours, ranges: hours.ranges.map((x, k) => (k === i ? { ...x, start: e.target.value } : x)) })} />
              <span>à</span>
              <input type="time" value={r.end} onChange={(e) => setHours({ ...hours, ranges: hours.ranges.map((x, k) => (k === i ? { ...x, end: e.target.value } : x)) })} />
              {hours.ranges.length > 1 && <button type="button" className="icon-btn" onClick={() => setHours({ ...hours, ranges: hours.ranges.filter((_, k) => k !== i) })} aria-label="Supprimer la plage">✕</button>}
            </div>
          ))}
          {hours.ranges.length < 3 && <button type="button" className="btn btn-small" onClick={() => setHours({ ...hours, ranges: [...hours.ranges, { start: "18:00", end: "21:00" }] })}>+ Plage horaire (équipe du soir…)</button>}
        </div>
        <div className="form-foot"><span className="spacer" /><button className="btn btn-primary">Enregistrer</button></div>
      </fieldset>
      {api.mode === "cloud" && edit && <SampleData api={api} />}
    </form>
  );
}

function SampleData({ api }: { api: Api }) {
  const [busy, setBusy] = useState(false);
  const load = async () => {
    if (!confirm("Ajouter un atelier d'exemple (5 machines, 4 opérateurs, une quinzaine de jobs, conditionnements) ? Pratique pour tester avant de saisir vos vrais jobs.")) return;
    setBusy(true);
    await attempt(async () => {
      const demo = makeDemo();
      const ids = new Map<string, string>();
      for (const m of demo.machines) {
        const { id, org_id: _o, ...rest } = m;
        ids.set(id, (await api.saveMachine(rest)).id);
      }
      for (const o of demo.operators) {
        await api.saveOperator({ name: o.name, role: o.role, active: true, machine_ids: o.machine_ids.map((x) => ids.get(x)!).filter(Boolean) }, demo.pins[o.id]);
      }
      const groups = new Map<string, string[]>();
      for (const c of demo.catalog) groups.set(`${c.kind}|${c.scope}`, [...(groups.get(`${c.kind}|${c.scope}`) || []), c.label]);
      for (const [k, labels] of groups) { const [kind, scope] = k.split("|"); await api.replaceCatalog(kind as CatalogItem["kind"], scope, null, labels); }
      for (const j of demo.jobs) {
        const ts = demo.tasks.filter((t) => t.job_id === j.id).map((t) => ({ operation: t.operation, fold_type: t.fold_type, format_in: t.format_in, format_out: t.format_out, paper: t.paper, grammage: t.grammage, pages: t.pages, params: t.params, packaging: t.packaging }));
        await api.saveJob(null, { of_number: j.of_number, client: j.client, title: j.title, quantity: j.quantity, due_at: j.due_at, priority: j.priority, sheets_ready_at: j.sheets_ready_at, notes: j.notes }, ts);
      }
    }, "Atelier d'exemple ajouté : codes opérateurs 1234, 2345, 3456 et 4567");
    setBusy(false);
    navigate("planning");
  };
  return (
    <div className="hint-box">
      <b>Envie d'essayer d'abord ?</b> Chargez un atelier d'exemple, puis cliquez sur « Optimiser » dans le planning.
      <button type="button" className="btn btn-small" disabled={busy} onClick={load}>{busy ? "Chargement…" : "Charger l'atelier d'exemple"}</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Machines et caractéristiques techniques
// ---------------------------------------------------------------------------
function specLine(m: Machine): string {
  return (MACHINE_SPECS[m.type] || []).map((f) => {
    const v = m.specs?.[f.key];
    if (v === undefined || v === null || v === "" || v === false || (Array.isArray(v) && !v.length)) return null;
    if (v === true) return f.label;
    return `${f.label.toLowerCase()} ${Array.isArray(v) ? v.join("/") : v}${f.unit ? ` ${f.unit}` : ""}`;
  }).filter(Boolean).slice(0, 4).join(" · ");
}

function Machines() {
  const { api, can } = useApp();
  const edit = can("machines");
  const { data, error, loading } = useData(api, () => api.machines(), []);
  const [form, setForm] = useState<Partial<Machine> | null>(null);
  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Loading />;
  return (
    <>
      {!edit && <ReadOnly what="les machines" />}
      <div className="toolbar"><p className="muted">Les cadences et temps de calage servent à estimer les durées. Les caractéristiques techniques évitent de planifier un travail qu'une machine ne sait pas faire.</p><span className="spacer" />
        {edit && <button className="btn btn-primary" onClick={() => setForm({ type: "plieuse", operations: ["pli"], formats: [], speed_per_hour: 6000, setup_min: 30, active: true, position: data.length, specs: {} })}>+ Ajouter une machine</button>}</div>
      {!data.length ? <Empty title="Aucune machine">Ajoutez vos plieuses, encarteuses-piqueuses, dos carré collé, raineurs-plieurs et pelliculeuses.</Empty> : (
        <div className="table-wrap card">
          <table className="table">
            <thead><tr><th>Machine</th><th>Travaux</th><th>Caractéristiques</th><th className="num">Cadence</th><th className="num">Calage</th><th /></tr></thead>
            <tbody>
              {data.map((m) => (
                <tr key={m.id} className={edit ? "click" : ""} onClick={() => edit && setForm(m)}>
                  <td><b>{m.name}</b><div className="muted small">{MACHINE_TYPE_LABEL[m.type]}</div></td>
                  <td>{m.operations.map((o) => OPERATION_LABEL[o]).join(", ")}<div className="muted small">{m.formats.length ? `Formats : ${m.formats.join(", ")}` : "Tous formats"}</div></td>
                  <td className="small">{specLine(m) || <span className="muted">—</span>}</td>
                  <td className="num">{m.speed_per_hour.toLocaleString("fr-FR")}<div className="muted small nowrap">{SPEED_UNIT[m.type]}</div></td>
                  <td className="num">{m.setup_min} min</td>
                  <td>{!m.active && <Badge>Désactivée</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {form && <MachineForm m={form} onClose={() => setForm(null)} />}
    </>
  );
}

export function SpecInput({ f, value, onChange }: { f: FieldDef; value: ParamValue | undefined; onChange: (v: ParamValue) => void }) {
  if (f.kind === "bool") {
    return <label className={`check ${value ? "on" : ""}`}><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />{f.label}</label>;
  }
  if (f.kind === "multi") {
    const list = Array.isArray(value) ? value : [];
    return (
      <Field label={f.label} hint={f.hint} wide>
        <div className="checks">
          {f.options!.map((o) => <label key={o} className={`check ${list.includes(o) ? "on" : ""}`}><input type="checkbox" checked={list.includes(o)} onChange={() => onChange(toggle(list, o))} />{o}</label>)}
        </div>
      </Field>
    );
  }
  if (f.kind === "select") {
    return (
      <Field label={f.label} hint={f.hint}>
        <select value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">—</option>
          {f.options!.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>
    );
  }
  return (
    <Field label={f.unit ? `${f.label} (${f.unit})` : f.label} hint={f.hint}>
      {f.kind === "number"
        ? <input type="number" min={0} value={typeof value === "number" ? value : ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />
        : <input value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || null)} />}
    </Field>
  );
}

function MachineForm({ m, onClose }: { m: Partial<Machine>; onClose: () => void }) {
  const { api } = useApp();
  const [f, setF] = useState<Partial<Machine>>({ specs: {}, ...m });
  const type = (f.type || "autre") as MachineType;
  const specs = MACHINE_SPECS[type] || [];
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!f.operations?.length) { toast("Cochez au moins un type de travail", "err"); return; }
    // ne garder que les caractéristiques du type choisi
    const keep = new Set(specs.map((x) => x.key));
    const clean = Object.fromEntries(Object.entries(f.specs || {}).filter(([k, v]) => keep.has(k) && v !== null && v !== ""));
    const ok = await attempt(() => api.saveMachine({ ...f, specs: clean, name: (f.name || "").trim() } as Machine), "Machine enregistrée");
    if (ok) onClose();
  };
  const bools = specs.filter((x) => x.kind === "bool");
  return (
    <Modal title={m.id ? m.name || "Machine" : "Nouvelle machine"} onClose={onClose} wide>
      <form className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field label="Nom" hint="Tel que l'atelier l'appelle"><input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Plieuse 1 · Stahl" required autoFocus /></Field>
          <Field label="Type">
            <select value={type} onChange={(e) => { const t = e.target.value as MachineType; setF({ ...f, type: t, operations: TYPE_OPS[t].length ? TYPE_OPS[t].slice(0, t === "raineur_plieur" ? 2 : 3) : f.operations }); }}>
              {(Object.keys(MACHINE_TYPE_LABEL) as MachineType[]).map((t) => <option key={t} value={t}>{MACHINE_TYPE_LABEL[t]}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Travaux possibles">
          <div className="checks">
            {(Object.keys(OPERATION_LABEL) as Operation[]).map((o) => (
              <label key={o} className={`check ${f.operations?.includes(o) ? "on" : ""}`}><input type="checkbox" checked={!!f.operations?.includes(o)} onChange={() => setF({ ...f, operations: toggle(f.operations || [], o) })} />{OPERATION_LABEL[o]}</label>
            ))}
          </div>
        </Field>
        <div className="grid-2">
          <Field label={`Cadence moyenne (${SPEED_UNIT[type]})`}><input type="number" min={1} value={f.speed_per_hour ?? 6000} onChange={(e) => setF({ ...f, speed_per_hour: Number(e.target.value) })} required /></Field>
          <Field label="Temps de calage par défaut (min)" hint="Quand le réglage change"><input type="number" min={0} value={f.setup_min ?? 30} onChange={(e) => setF({ ...f, setup_min: Number(e.target.value) })} required /></Field>
        </div>
        <fieldset className="spec-box">
          <legend>Caractéristiques techniques · {MACHINE_TYPE_LABEL[type]}</legend>
          <div className="grid-3">
            {specs.filter((x) => x.kind !== "bool" && x.kind !== "multi").map((x) => <SpecInput key={x.key} f={x} value={f.specs?.[x.key]} onChange={(v) => setF({ ...f, specs: { ...f.specs, [x.key]: v } })} />)}
          </div>
          {specs.filter((x) => x.kind === "multi").map((x) => <SpecInput key={x.key} f={x} value={f.specs?.[x.key]} onChange={(v) => setF({ ...f, specs: { ...f.specs, [x.key]: v } })} />)}
          {bools.length > 0 && <div className="checks">{bools.map((x) => <SpecInput key={x.key} f={x} value={f.specs?.[x.key]} onChange={(v) => setF({ ...f, specs: { ...f.specs, [x.key]: v } })} />)}</div>}
        </fieldset>
        <Field label="Formats d'entrée acceptés" hint="Aucun coché = tous les formats">
          <div className="checks">
            {FORMATS.map((x) => (
              <label key={x} className={`check ${f.formats?.includes(x) ? "on" : ""}`}><input type="checkbox" checked={!!f.formats?.includes(x)} onChange={() => setF({ ...f, formats: toggle(f.formats || [], x) })} />{x}</label>
            ))}
          </div>
        </Field>
        <label className="check-line"><input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Machine en service</label>
        <div className="modal-foot">
          {m.id && <button type="button" className="btn btn-ghost danger" onClick={async () => { if (confirm("Supprimer cette machine ? Ses jobs repasseront « à planifier ».")) { await attempt(() => api.deleteMachine(m.id!), "Machine supprimée"); onClose(); } }}>Supprimer</button>}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary">Enregistrer</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Listes modifiables (conditionnements, aléas)
// ---------------------------------------------------------------------------
function ListEditor({ items, onChange, suggestions = [], placeholder, disabled, defaults = false }: {
  items: string[]; onChange: (v: string[]) => Promise<void> | void; suggestions?: string[]; placeholder: string; disabled?: boolean; defaults?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const add = async (label: string) => {
    const v = label.trim();
    if (!v) return;
    if (items.some((x) => x.toLowerCase() === v.toLowerCase())) { toast("Déjà dans la liste", "info"); return; }
    await onChange([...items, v]);
    setDraft("");
  };
  const move = (i: number, d: number) => { const a = [...items]; const [x] = a.splice(i, 1); a.splice(i + d, 0, x); onChange(a); };
  const left = suggestions.filter((x) => !items.includes(x));
  return (
    <div className="list-editor">
      {items.length ? (
        <ul className="le-items">
          {items.map((x, i) => (
            <li key={x} className={defaults ? "is-default" : ""}>
              <span>{x}</span>
              {!disabled && <>
                <button type="button" className="icon-btn" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Monter ${x}`}>↑</button>
                <button type="button" className="icon-btn" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label={`Descendre ${x}`}>↓</button>
                <button type="button" className="icon-btn" onClick={() => onChange(items.filter((y) => y !== x))} aria-label={`Retirer ${x}`}>✕</button>
              </>}
            </li>
          ))}
        </ul>
      ) : <p className="muted small">Aucun élément pour l'instant.</p>}
      {!disabled && (
        <form className="le-add" onSubmit={(e) => { e.preventDefault(); add(draft); }}>
          <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} maxLength={80} />
          <button className="btn" disabled={!draft.trim()}>Ajouter</button>
        </form>
      )}
      {!disabled && left.length > 0 && (
        <div className="le-sugg"><span className="muted small">Suggestions :</span>{left.map((x) => <button type="button" key={x} className="chip" onClick={() => add(x)}>+ {x}</button>)}</div>
      )}
    </div>
  );
}

function usedTypes(machines: Machine[]): MachineType[] {
  const seen = machines.map((m) => m.type);
  return (Object.keys(MACHINE_TYPE_LABEL) as MachineType[]).filter((t) => seen.includes(t));
}

function Packaging() {
  const { api, can } = useApp();
  const edit = can("machines");
  const { data, error, loading } = useData(api, async () => ({ machines: await api.machines(), items: await api.catalog("conditionnement") }), []);
  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Loading />;
  const types = usedTypes(data.machines);
  return (
    <>
      {!edit && <ReadOnly what="les conditionnements" />}
      <p className="muted intro">Vos façons de livrer le travail à la sortie de chaque poste. Elles se choisissent dans chaque étape de job et s'affichent en grand sur la tablette.</p>
      {!types.length ? <Empty title="Ajoutez d'abord vos machines">Les conditionnements se règlent par type de machine.</Empty> : (
        <div className="cat-grid">
          {types.map((t) => (
            <section key={t} className="card cat-card">
              <h2>{MACHINE_TYPE_LABEL[t]}</h2>
              <ListEditor disabled={!edit} placeholder="Ex. Paquets de 100 sous film"
                items={data.items.filter((c) => c.scope === t).map((c) => c.label)}
                suggestions={PACKAGING_SUGGESTIONS[t] || []}
                onChange={(labels) => attempt(() => api.replaceCatalog("conditionnement", t, null, labels), "Liste enregistrée").then(() => undefined)} />
            </section>
          ))}
        </div>
      )}
    </>
  );
}

function Aleas() {
  const { api, can } = useApp();
  const edit = can("machines");
  const { data, error, loading } = useData(api, async () => ({ machines: await api.machines(), items: await api.catalog("alea") }), []);
  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Loading />;
  const types = usedTypes(data.machines);
  return (
    <>
      {!edit && <ReadOnly what="les aléas" />}
      <p className="muted intro">Les boutons « Signaler un aléa » de chaque tablette. Réglez la liste par type de poste, puis ajoutez si besoin des aléas propres à une machine.</p>
      {!types.length ? <Empty title="Ajoutez d'abord vos machines" /> : types.map((t) => {
        const custom = data.items.filter((c) => !c.machine_id && c.scope === t).map((c) => c.label);
        const list = custom.length ? custom : DEFAULT_ALEAS[t];
        return (
          <section key={t} className="card cat-card wide">
            <div className="cat-head">
              <h2>{MACHINE_TYPE_LABEL[t]}</h2>
              {!custom.length ? <Badge>Liste par défaut</Badge> : edit && <button className="link small" onClick={() => attempt(() => api.replaceCatalog("alea", t, null, []), "Liste par défaut rétablie")}>Revenir à la liste par défaut</button>}
            </div>
            <ListEditor disabled={!edit} defaults={!custom.length} placeholder="Ex. Margeur 2, Contrôle couleur…" items={list}
              onChange={(labels) => attempt(() => api.replaceCatalog("alea", t, null, labels), "Aléas enregistrés").then(() => undefined)} />
            <div className="cat-machines">
              {data.machines.filter((m) => m.type === t).map((m) => (
                <div key={m.id} className="cat-machine">
                  <b>En plus sur {m.name}</b>
                  <ListEditor disabled={!edit} placeholder="Aléa propre à ce poste"
                    items={data.items.filter((c) => c.machine_id === m.id).map((c) => c.label)}
                    onChange={(labels) => attempt(() => api.replaceCatalog("alea", "", m.id, labels), "Aléas du poste enregistrés").then(() => undefined)} />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Opérateurs (tablettes)
// ---------------------------------------------------------------------------
function Operators() {
  const { api, can } = useApp();
  const edit = can("equipe");
  const { data, error, loading } = useData(api, async () => ({ ops: await api.operators(), machines: await api.machines() }), []);
  const [form, setForm] = useState<Partial<Operator> | null>(null);
  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Loading />;
  return (
    <>
      {!edit && <ReadOnly what="les opérateurs" />}
      <div className="toolbar"><p className="muted">Sur la tablette, chacun choisit son nom et tape son code à 4 chiffres. Un conducteur ne peut agir que sur les jobs de son poste qui ne sont pas attribués à un collègue, dans l'ordre publié ; un chef peut tout faire sur tous les postes.</p><span className="spacer" />
        {edit && <button className="btn btn-primary" onClick={() => setForm({ machine_ids: [], active: true, role: "conducteur" })}>+ Ajouter un opérateur</button>}</div>
      {!data.ops.length ? <Empty title="Aucun opérateur">Ajoutez vos conducteurs et chefs d'atelier.</Empty> : (
        <div className="table-wrap card">
          <table className="table">
            <thead><tr><th>Nom</th><th>Rôle</th><th>Postes</th><th /></tr></thead>
            <tbody>
              {data.ops.map((o) => (
                <tr key={o.id} className={edit ? "click" : ""} onClick={() => edit && setForm(o)}>
                  <td><span className="avatar">{o.name.slice(0, 1).toUpperCase()}</span><b>{o.name}</b></td>
                  <td><Badge tone={o.role === "conducteur" ? "" : "blue"}>{OPERATOR_ROLE_LABEL[o.role || "conducteur"]}</Badge></td>
                  <td>{o.role !== "conducteur" || !o.machine_ids.length ? "Tous" : o.machine_ids.map((id) => data.machines.find((m) => m.id === id)?.name).filter(Boolean).join(", ")}</td>
                  <td>{!o.active && <Badge>Inactif</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {form && <OperatorForm o={form} machines={data.machines} onClose={() => setForm(null)} />}
    </>
  );
}

function OperatorForm({ o, machines, onClose }: { o: Partial<Operator>; machines: Machine[]; onClose: () => void }) {
  const { api } = useApp();
  const [f, setF] = useState<Partial<Operator>>({ role: "conducteur", ...o });
  const [pin, setPin] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!o.id && !/^[0-9]{4}$/.test(pin)) { toast("Choisissez un code à 4 chiffres", "err"); return; }
    if (pin && !/^[0-9]{4}$/.test(pin)) { toast("Le code doit faire 4 chiffres", "err"); return; }
    const ok = await attempt(() => api.saveOperator({ ...f, name: (f.name || "").trim() } as Operator, pin || undefined), "Opérateur enregistré");
    if (ok) onClose();
  };
  const ids = f.machine_ids || [];
  return (
    <Modal title={o.id ? o.name || "Opérateur" : "Nouvel opérateur"} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <div className="grid-2">
          <Field label="Prénom ou nom"><input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} required autoFocus /></Field>
          <Field label={o.id ? "Nouveau code (4 chiffres)" : "Code (4 chiffres)"} hint={o.id ? "Laisser vide pour garder l'actuel" : "À communiquer à l'opérateur"}>
            <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="••••" />
          </Field>
        </div>
        <Field label="Rôle sur la tablette">
          <div className="role-pick">
            {(Object.keys(OPERATOR_ROLE_LABEL) as OperatorRole[]).map((r) => (
              <label key={r} className={`role-opt ${f.role === r ? "on" : ""}`}>
                <input type="radio" name="role" checked={f.role === r} onChange={() => setF({ ...f, role: r })} />
                <b>{OPERATOR_ROLE_LABEL[r]}</b>
                <span>{r === "conducteur" ? "Ses jobs, dans l'ordre publié, sur ses postes" : "Tous les postes, peut changer l'ordre et reprendre un job"}</span>
              </label>
            ))}
          </div>
        </Field>
        {f.role === "conducteur" && (
          <Field label="Postes autorisés" hint="Aucun coché = tous les postes">
            <div className="checks">
              {machines.map((m) => (
                <label key={m.id} className={`check ${ids.includes(m.id) ? "on" : ""}`}><input type="checkbox" checked={ids.includes(m.id)} onChange={() => setF({ ...f, machine_ids: toggle(ids, m.id) })} />{m.name}</label>
              ))}
            </div>
          </Field>
        )}
        <label className="check-line"><input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Actif</label>
        <div className="modal-foot">
          {o.id && <button type="button" className="btn btn-ghost danger" onClick={async () => { if (confirm("Supprimer cet opérateur ?")) { await attempt(() => api.deleteOperator(o.id!), "Opérateur supprimé"); onClose(); } }}>Supprimer</button>}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary">Enregistrer</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
function Tablets() {
  const { api, can } = useApp();
  const edit = can("equipe");
  const { data, error, loading } = useData(api, async () => ({ devices: await api.devices(), machines: await api.machines() }), [], 15_000);
  const [code, setCode] = useState<{ machine: string; code: string } | null>(null);
  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Loading />;
  const posteUrl = `${location.origin}${location.pathname}#/poste`;
  return (
    <>
      {!edit && <ReadOnly what="les tablettes" />}
      <div className="card pad-l">
        <h2>Installer une tablette au poste</h2>
        <ol className="howto">
          <li>Sur la tablette, ouvrez <b className="mono">{posteUrl}</b> et ajoutez la page à l'écran d'accueil.</li>
          <li>Ici, cliquez sur « Associer une tablette » en face de la machine : un code à 8 chiffres s'affiche (valable 15 minutes).</li>
          <li>Tapez ce code sur la tablette. Elle reste rattachée à la machine, sans compte ni mot de passe.</li>
        </ol>
        {api.mode === "demo" && <p className="hint-box">En démonstration, la tablette peut aussi choisir directement sa machine : ouvrez <a href="#/poste" target="_blank" rel="noopener">le poste tablette</a> dans un autre onglet.</p>}
      </div>
      <div className="machines-grid">
        {data.machines.map((m) => {
          const ds = data.devices.filter((d) => d.machine_id === m.id);
          return (
            <div key={m.id} className="card mcard">
              <div className="mcard-head"><b>{m.name}</b><Badge tone={ds.length ? "teal" : ""}>{ds.length ? `${ds.length} tablette${ds.length > 1 ? "s" : ""}` : "Aucune tablette"}</Badge></div>
              {ds.map((d) => (
                <div key={d.id} className="device-row">
                  <span>{d.name}<span className="muted small"> · vue {d.last_seen_at ? fmtDateTime(d.last_seen_at) : "jamais"}</span></span>
                  {edit && <button className="btn btn-small btn-ghost danger" onClick={async () => { if (confirm("Retirer cette tablette ? Elle devra être associée à nouveau.")) await attempt(() => api.deleteDevice(d.id), "Tablette retirée"); }}>Retirer</button>}
                </div>
              ))}
              {edit && <button className="btn" onClick={async () => { const c = await attempt(() => api.createPairingCode(m.id)); if (c) setCode({ machine: m.name, code: c }); }}>Associer une tablette</button>}
            </div>
          );
        })}
      </div>
      {code && (
        <Modal title={`Associer une tablette · ${code.machine}`} onClose={() => setCode(null)}>
          <p>Sur la tablette, ouvrez la page du poste et tapez ce code :</p>
          <div className="big-code">{code.code.slice(0, 4)} {code.code.slice(4)}</div>
          <p className="muted small">Valable 15 minutes, une seule fois. Après 5 codes erronés, la tablette doit attendre 15 minutes.</p>
        </Modal>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Équipe : comptes, invitations, droits par rôle
// ---------------------------------------------------------------------------
const WEB_ROLES = Object.keys(ROLE_LABEL) as Role[];

function Team() {
  const { api, org, role, refreshOrg } = useApp();
  const admin = role === "admin";
  const { data, error, loading } = useData(api, async () => ({ members: await api.members(), invites: admin ? await api.invitations() : [] }), [admin]);
  const [email, setEmail] = useState("");
  const [invRole, setInvRole] = useState<Role>("chef");
  const [busy, setBusy] = useState(false);
  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Loading />;
  const me = api.session()?.user_id;
  const admins = data.members.filter((m) => m.role === "admin").length;
  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    await attempt(() => api.invite(email, invRole), api.mode === "demo" ? "Invitation enregistrée (démonstration : aucun mail envoyé)" : `Invitation envoyée à ${email.trim()}`);
    setBusy(false);
    setEmail("");
  };
  const perms = org.permissions || DEFAULT_PERMISSIONS;
  const setPerm = async (r: Role, p: Permission, on: boolean) => {
    const cur = perms[r] || [];
    const next = { ...perms, [r]: on ? [...cur, p] : cur.filter((x) => x !== p) };
    await attempt(() => api.updateOrg({ permissions: next }), "Droits enregistrés");
    await refreshOrg();
  };
  return (
    <>
      <section className="card pad-l">
        <h2>Comptes ({data.members.length})</h2>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Nom</th><th>Adresse</th><th>Rôle</th>{admin && <th />}</tr></thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.user_id}>
                  <td><span className="avatar">{(m.name || m.email).slice(0, 1).toUpperCase()}</span>{m.name || "—"}{m.user_id === me && <span className="muted small"> (vous)</span>}</td>
                  <td>{m.email}</td>
                  <td>{admin && m.user_id !== me
                    ? <select className="inline-select" value={m.role} onChange={(e) => attempt(() => api.updateMember(m.user_id, e.target.value as Role), "Rôle modifié")}>{WEB_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}</select>
                    : <Badge tone={m.role === "admin" ? "orange" : ""}>{ROLE_LABEL[m.role] || m.role}</Badge>}</td>
                  {admin && <td className="num">{m.user_id !== me && !(m.role === "admin" && admins < 2) && <button className="btn btn-small btn-ghost danger" onClick={() => { if (confirm(`Retirer l'accès de ${m.email} ?`)) attempt(() => api.removeMember(m.user_id), "Accès retiré"); }}>Retirer</button>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {admin && (
          <>
            <h2 className="section-title">Inviter un collègue</h2>
            <form className="invite" onSubmit={invite}>
              <input type="email" placeholder="adresse@imprimerie.fr" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <select value={invRole} onChange={(e) => setInvRole(e.target.value as Role)}>{WEB_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}</select>
              <button className="btn btn-primary" disabled={busy}>{busy ? "Envoi…" : "Inviter"}</button>
            </form>
            <p className="muted small">La personne reçoit un lien de connexion. Elle peut aussi créer son compte avec cette adresse : elle rejoint alors directement {org.name}.</p>
            {data.invites.length > 0 && (
              <ul className="invites">
                {data.invites.map((i) => (
                  <li key={i.email}><span>{i.email}</span><Badge>{ROLE_LABEL[i.role]}</Badge><span className="muted small">en attente</span>
                    <button className="btn btn-small btn-ghost danger" onClick={() => attempt(() => api.cancelInvite(i.email), "Invitation annulée")}>Annuler</button></li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      <section className="card pad-l">
        <h2>Droits par rôle</h2>
        <p className="muted small">Tout le monde peut consulter le planning, les jobs et l'atelier. Cochez ce que chaque rôle peut modifier. L'administrateur a tous les droits, dont la gestion des comptes.</p>
        <div className="table-wrap">
          <table className="table perms">
            <thead><tr><th>Rôle</th>{(Object.keys(PERMISSION_LABEL) as Permission[]).map((p) => <th key={p} title={PERMISSION_LABEL[p].hint}>{PERMISSION_LABEL[p].label}</th>)}</tr></thead>
            <tbody>
              {WEB_ROLES.map((r) => (
                <tr key={r}>
                  <td><b>{ROLE_LABEL[r]}</b></td>
                  {(Object.keys(PERMISSION_LABEL) as Permission[]).map((p) => {
                    const on = r === "admin" || (perms[r] || []).includes(p);
                    return (
                      <td key={p}>
                        <label className={`switch ${on ? "on" : ""} ${!admin || r === "admin" ? "locked" : ""}`}>
                          <input type="checkbox" checked={on} disabled={!admin || r === "admin"} onChange={(e) => setPerm(r, p, e.target.checked)} aria-label={`${ROLE_LABEL[r]} : ${PERMISSION_LABEL[p].label}`} />
                          <i />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!admin && <p className="muted small">Seul un administrateur peut changer ces droits.</p>}
        <p className="muted small">Sur les tablettes, les rôles des opérateurs se règlent dans <a href="#/parametres/operateurs">Opérateurs</a>.</p>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Export des données (Excel ou CSV)
// ---------------------------------------------------------------------------
const EXPORT_CONTENT: [string, string][] = [
  ["Jobs", "tous les jobs, avec leurs temps réels, aléas et gâche cumulés"],
  ["Tâches", "chaque étape : réglages, planning prévu et publié, temps réels de calage, production et pause, quantités, cadence"],
  ["Aléas", "chaque aléa signalé sur les postes : machine, nature, commentaire, durée"],
  ["Temps", "tous les pointages des tablettes (début de calage, production, pauses, fin), avec la durée de chaque segment"],
  ["Machines", "synthèse par poste : temps passés, aléas, quantités produites"],
  ["Opérateurs", "rôles, postes, nombre de pointages et d'aléas"],
];

const fileSlug = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function ExportTab() {
  const { api, org } = useApp();
  const [busy, setBusy] = useState<"xlsx" | "csv" | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const run = async (format: "xlsx" | "csv") => {
    setBusy(format);
    setDone(null);
    await attempt(async () => {
      const now = new Date();
      const sheets = buildSheets(await api.exportData(), now);
      const name = exportFileName(org.name, now);
      const tz = org.timezone || "Europe/Paris";
      if (format === "xlsx") {
        download(`${name}.xlsx`, buildXlsx(sheets, tz), "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      } else {
        download(`${name}-csv.zip`, zip(sheets.map((s) => ({ name: `${fileSlug(s.name)}.csv`, data: buildCsv(s, tz) })), now), "application/zip");
      }
      setDone(sheets.map((s) => `${s.name} : ${s.rows.length}`).join(" · "));
    }, "Export téléchargé");
    setBusy(null);
  };
  return (
    <div className="card form-card">
      <h2 className="section-title" style={{ marginTop: 0 }}>Exporter toutes les données de l'atelier</h2>
      <p className="muted small">
        Tout l'historique de l'imprimerie « {org.name} », sans limite de date. Les heures sont à l'heure de l'atelier et les durées en minutes.
        Les temps réels sont recalculés à partir des pointages des tablettes.
      </p>
      <ul className="small">
        {EXPORT_CONTENT.map(([k, v]) => <li key={k}><b>{k}</b> : {v}</li>)}
      </ul>
      <div className="form-foot">
        <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => run("xlsx")}>
          {busy === "xlsx" ? "Préparation…" : "Télécharger en Excel (.xlsx)"}
        </button>
        <button type="button" className="btn" disabled={!!busy} onClick={() => run("csv")}>
          {busy === "csv" ? "Préparation…" : "Télécharger en CSV (.zip)"}
        </button>
        <span className="spacer" />
      </div>
      <p className="muted small">
        Excel : un onglet par sujet, avec filtres. CSV : un fichier par sujet, séparateur point-virgule, lisible directement par Excel en français.
      </p>
      {done && <p className="small">Dernier export (lignes) — {done}</p>}
    </div>
  );
}
